import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ThemeToggle from '../components/ThemeToggle';
import { 
  ChatBubbleLeftRightIcon, 
  UserGroupIcon, 
  PlusCircleIcon, 
  ArrowRightCircleIcon,
  CheckCircleIcon,
  PencilIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

function Home() {
  const navigate = useNavigate();
  const [nickname, setNickname] = useState('');
  const [error, setError] = useState('');
  const [isNicknameSaved, setIsNicknameSaved] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditingNickname, setIsEditingNickname] = useState(false);

  useEffect(() => {
    // Check if a nickname already exists in local storage
    const existingNickname = localStorage.getItem('chatNickname');
    if (existingNickname) {
      setNickname(existingNickname);
      setIsNicknameSaved(true);
    }
  }, [navigate]);

  const handleNicknameSubmit = () => {
    if (!nickname.trim()) {
      setError('Please enter a nickname');
      return;
    }
    if (nickname.trim().length < 3) {
      setError('Nickname must be at least 3 characters long');
      return;
    }
    
    setIsLoading(true);
    
    // Simulate a small delay for better UX
    setTimeout(() => {
      localStorage.setItem('chatNickname', nickname.trim());
      setIsNicknameSaved(true);
      setIsEditingNickname(false);
      setError('');
      setIsLoading(false);
      toast.success(`Nickname ${isEditingNickname ? 'updated' : 'saved'}! You can now join or create a room.`);
    }, 600);
  };

  const handleEditNickname = () => {
    setIsEditingNickname(true);
    setIsNicknameSaved(false);
  };

  const handleCancelEdit = () => {
    // Restore the nickname from localStorage
    const existingNickname = localStorage.getItem('chatNickname');
    if (existingNickname) {
      setNickname(existingNickname);
    }
    setIsEditingNickname(false);
    setIsNicknameSaved(true);
    setError('');
  };

  const handleCreateRoom = () => {
    if (!isNicknameSaved) {
      toast.error('Please enter and save your nickname first');
      return;
    }
    navigate('/create-room');
  };

  const handleJoinRoom = () => {
    if (!isNicknameSaved) {
      toast.error('Please enter and save your nickname first');
      return;
    }
    navigate('/join-room');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-900 dark:via-indigo-950/30 dark:to-purple-950/30 flex flex-col items-center justify-center relative overflow-hidden px-4 py-8 transition-colors">
      {/* Enhanced background elements */}
      <div className="absolute inset-0 bg-grid-pattern opacity-5 dark:opacity-10"></div>
      <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-200/20 via-transparent to-transparent dark:from-blue-800/10"></div>
      
      {/* Theme toggle in top right */}
      <div className="absolute top-5 right-5 z-20">
        <ThemeToggle />
      </div>
      
      <div className="relative z-10 w-full max-w-4xl mx-auto flex flex-col items-center">
        {/* Header section with improved typography */}
        <div className="text-center mb-10 sm:mb-14 animate-fade-in">
          <h1 className="text-5xl sm:text-7xl font-extrabold tracking-tight text-gray-800 dark:text-white mb-5 transition-colors drop-shadow-sm">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-400 dark:to-indigo-400">Chat Sphere</span>
          </h1>
          <p className="text-gray-600 dark:text-gray-300 max-w-2xl mx-auto text-lg md:text-xl font-light transition-colors">
            Connect with others in real-time through our interactive chat platform
          </p>
        </div>

        {/* Main content container with improved shadows and borders */}
        <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-2xl shadow-2xl dark:shadow-black/30 border border-gray-100 dark:border-gray-700 p-8 sm:p-10 mb-10 animate-slide-in transition-all w-full">
          {/* Step 1: Nickname section */}
          <div className="mb-10">
            <div className="flex items-center mb-5">
              <div className="w-9 h-9 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mr-3 shadow-sm transition-colors">
                <span className="text-blue-600 dark:text-blue-400 font-bold">1</span>
              </div>
              <h2 className="text-2xl font-semibold text-gray-800 dark:text-white transition-colors">
                {isEditingNickname ? 'Edit your nickname' : 'Choose your nickname'}
              </h2>
            </div>
            
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <div className="relative w-full sm:flex-1">
                <input
                  type="text"
                  value={nickname}
                  onChange={(e) => {
                    setNickname(e.target.value);
                    setError('');
                  }}
                  placeholder="Enter your nickname..."
                  className={`px-5 py-3.5 border-2 rounded-lg w-full focus:outline-none transition-all
                    text-base dark:bg-gray-700 dark:text-white dark:placeholder-gray-400
                    ${error 
                      ? 'border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/20 shadow-md shadow-red-200 dark:shadow-red-900/10' 
                      : isNicknameSaved && !isEditingNickname
                        ? 'border-green-300 bg-green-50 dark:border-green-700 dark:bg-green-900/20 shadow-md shadow-green-200 dark:shadow-green-900/10' 
                        : 'border-gray-200 dark:border-gray-600 focus:border-blue-500 dark:focus:border-blue-400 focus:shadow-md focus:shadow-blue-200 dark:focus:shadow-blue-900/10'}`}
                  disabled={isNicknameSaved && !isEditingNickname && !isLoading}
                />
                {isNicknameSaved && !isEditingNickname && (
                  <CheckCircleIcon className="absolute right-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-green-500 dark:text-green-400" />
                )}
              </div>
              
              {isNicknameSaved && !isEditingNickname ? (
                <button
                  onClick={handleEditNickname}
                  className="px-6 py-3.5 rounded-lg font-medium transition-all duration-200 whitespace-nowrap text-base sm:text-base
                    bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-800/40 hover:shadow-md"
                >
                  <span className="flex items-center">
                    <PencilIcon className="h-5 w-5 mr-1" />
                    Edit
                  </span>
                </button>
              ) : (
                <div className="flex gap-2 w-full sm:w-auto">
                  {isEditingNickname && (
                    <button
                      onClick={handleCancelEdit}
                      className="px-4 py-3.5 rounded-lg font-medium transition-all duration-200 whitespace-nowrap text-base sm:text-base
                        bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 flex-1 sm:flex-auto"
                    >
                      Cancel
                    </button>
                  )}
                  <button
                    onClick={handleNicknameSubmit}
                    disabled={isLoading}
                    className={`px-6 py-3.5 rounded-lg font-medium transition-all duration-200 whitespace-nowrap text-base sm:text-base flex-1 sm:flex-auto
                      ${isLoading
                        ? 'bg-blue-400 dark:bg-blue-500 text-white cursor-wait shadow-md shadow-blue-300/50 dark:shadow-blue-800/30'
                        : 'bg-blue-500 dark:bg-blue-600 text-white hover:bg-blue-600 dark:hover:bg-blue-700 hover:shadow-lg hover:shadow-blue-500/30 dark:hover:shadow-blue-600/30 active:scale-[0.98]'}`}
                  >
                    {isLoading ? (
                      <span className="flex items-center justify-center">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Saving...
                      </span>
                    ) : (
                      isEditingNickname ? 'Update Nickname' : 'Save Nickname'
                    )}
                  </button>
                </div>
              )}
            </div>
            
            {error && <p className="text-red-500 dark:text-red-400 text-sm mt-3 ml-1">{error}</p>}
            
            {isNicknameSaved && !isEditingNickname && (
              <p className="text-green-600 dark:text-green-400 text-sm mt-3 ml-1">
                Hello, <span className="font-medium">{nickname}</span>! You're ready to chat.
              </p>
            )}
          </div>

          {/* Step 2: Room options with improved cards */}
          <div>
            <div className="flex items-center mb-5">
              <div className="w-9 h-9 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mr-3 shadow-sm transition-colors">
                <span className="text-blue-600 dark:text-blue-400 font-bold">2</span>
              </div>
              <h2 className="text-2xl font-semibold text-gray-800 dark:text-white transition-colors">Choose an option</h2>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {/* Create Room Card */}
              <button 
                onClick={handleCreateRoom}
                disabled={!isNicknameSaved || isEditingNickname}
                className={`w-full text-left p-6 rounded-xl transition-all duration-300 border group relative overflow-hidden
                  ${(!isNicknameSaved || isEditingNickname)
                    ? 'bg-gray-50 dark:bg-gray-700/50 border-gray-100 dark:border-gray-600 cursor-not-allowed opacity-60' 
                    : 'bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700 shadow-lg hover:shadow-xl hover:border-blue-200 dark:hover:border-blue-700 hover:-translate-y-1 cursor-pointer'}`}
              >
                {isNicknameSaved && !isEditingNickname && <div className="absolute inset-0 bg-gradient-to-br from-blue-50/80 to-transparent dark:from-blue-900/10 dark:to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>}
                <div className="flex flex-col items-center gap-5 relative z-10">
                  <div className={`p-4 rounded-full transition-all duration-300
                    ${(!isNicknameSaved || isEditingNickname)
                      ? 'bg-gray-100 dark:bg-gray-600' 
                      : 'bg-blue-50 dark:bg-blue-900/30 group-hover:bg-blue-100 dark:group-hover:bg-blue-800/40 group-hover:shadow-md group-hover:shadow-blue-300/30 dark:group-hover:shadow-blue-900/20'}`}>
                    <PlusCircleIcon className={`h-10 w-10 ${(!isNicknameSaved || isEditingNickname)
                      ? 'text-gray-400 dark:text-gray-500' 
                      : 'text-blue-500 dark:text-blue-400'}`} />
                  </div>
                  <h2 className={`text-2xl font-semibold ${(!isNicknameSaved || isEditingNickname)
                    ? 'text-gray-400 dark:text-gray-500' 
                    : 'text-gray-800 dark:text-white'}`}>
                    Create Room
                  </h2>
                  <p className={`text-base text-center ${(!isNicknameSaved || isEditingNickname)
                    ? 'text-gray-400 dark:text-gray-500' 
                    : 'text-gray-500 dark:text-gray-400'}`}>
                    Start a new chat room and invite others to join
                  </p>
                </div>
              </button>

              {/* Join Room Card */}
              <button 
                onClick={handleJoinRoom}
                disabled={!isNicknameSaved || isEditingNickname}
                className={`w-full text-left p-6 rounded-xl transition-all duration-300 border group relative overflow-hidden
                  ${(!isNicknameSaved || isEditingNickname)
                    ? 'bg-gray-50 dark:bg-gray-700/50 border-gray-100 dark:border-gray-600 cursor-not-allowed opacity-60' 
                    : 'bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700 shadow-lg hover:shadow-xl hover:border-green-200 dark:hover:border-green-700 hover:-translate-y-1 cursor-pointer'}`}
              >
                {isNicknameSaved && !isEditingNickname && <div className="absolute inset-0 bg-gradient-to-br from-green-50/80 to-transparent dark:from-green-900/10 dark:to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>}
                <div className="flex flex-col items-center gap-5 relative z-10">
                  <div className={`p-4 rounded-full transition-all duration-300
                    ${(!isNicknameSaved || isEditingNickname)
                      ? 'bg-gray-100 dark:bg-gray-600' 
                      : 'bg-green-50 dark:bg-green-900/30 group-hover:bg-green-100 dark:group-hover:bg-green-800/40 group-hover:shadow-md group-hover:shadow-green-300/30 dark:group-hover:shadow-green-900/20'}`}>
                    <ArrowRightCircleIcon className={`h-10 w-10 ${(!isNicknameSaved || isEditingNickname)
                      ? 'text-gray-400 dark:text-gray-500' 
                      : 'text-green-500 dark:text-green-400'}`} />
                  </div>
                  <h2 className={`text-2xl font-semibold ${(!isNicknameSaved || isEditingNickname)
                    ? 'text-gray-400 dark:text-gray-500' 
                    : 'text-gray-800 dark:text-white'}`}>
                    Join Room
                  </h2>
                  <p className={`text-base text-center ${(!isNicknameSaved || isEditingNickname)
                    ? 'text-gray-400 dark:text-gray-500' 
                    : 'text-gray-500 dark:text-gray-400'}`}>
                    Enter an existing room code to join the conversation
                  </p>
                </div>
              </button>
            </div>
          </div>
        </div>
        
        {/* Footer with version info and credits */}
        <div className="text-center mt-2 text-gray-400 dark:text-gray-500 text-sm">
          <p>Chat Sphere v1.0 • Made with ❤️</p>
        </div>
      </div>
    </div>
  );
}

export default Home;
